import { usePageTitle } from "../../../../../shared/hooks/usePageTitle";
import { useEffect } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { isPossibleSlug } from "../../lib/slug";
import { useBookingProfile } from "../../features/profile/useBookingProfile";
import type { PublicProfessionalProfile } from "../../features/profile/operations";
import NotFound from "../NotFound";
import { ConsentBanner } from "../../components/Consent";
import Promo from "../../components/Promo";
import { PageHead, PageShell } from "../../components/PageShell";
import { ServiceStepSkeleton } from "../Book/StepService";
import { signInChunk } from "../Book/lazySteps";
import { useTranslation } from "../../i18n/useTranslation";
import { LoadFailed } from "../../components/LoadFailed";
import { SignOutContext } from "../../components/SessionRow";
import { useIdentity } from "../../auth/IdentityProvider";
import { useBookSignOut } from "../../auth/useBookSignOut";

// book.uselura.com/<slug> — a professional's public booking page.
//
// Layout follows docs/design/lura-book-v1 (Profile, ProfileLoading,
// ProfileNotFound). The four states below are backend's resolver contract, not
// a UI choice — see docs/architecture/routes-book.md.

/*
 * LOADING. A ready professional who accepts guests resolves straight to the
 * service step, so the placeholder is that step's shape (StepService
 * ServiceStepSkeleton), not the old profile hero: a hero the visitor never
 * gets, followed by a jump to a different layout, was the reflow the skeleton
 * exists to prevent. The hidden h1 and the one polite "Loading…" are the
 * accessible half and live inside it; `booking-profile` + `data-state` stay
 * on <main> for the suites.
 */
function Loading() {
  return <ServiceStepSkeleton testId="booking-profile" />;
}

/**
 * UNAVAILABLE — one message for mistyped, expired, retired and removed.
 *
 * Product ruled these indistinguishable on purpose: separating them makes a
 * takedown publicly detectable. Backend's own suggested copy is used verbatim
 * so the three surfaces say the same thing. Do NOT add detail here.
 */
/*
 * THE PUBLIC LINK'S GENERIC REFUSAL. Web v2 · 04 · "Not available".
 *
 * One answer for an unknown, reserved, revoked or tombstoned link: no name, no
 * cause, no retry. Anything more helpful turns the page into a way of probing
 * which links exist. The tell is whether a name appears: UNAVAILABLE never
 * can, NOT_BOOKABLE always should.
 *
 * v2's shape (heading, sentence, hint) with the EXISTING words, not v2's. The
 * words come from Backend's resolver documentation so that Web, Mobile and
 * the API say the same thing about the same link, and changing them on one
 * surface alone breaks that. v2's closing line, "the link in your
 * confirmation email still works", is not used at all: whether a confirmation
 * was sent is a notification outcome this surface never reads, and whether
 * that link works is decided per token.
 */
function Unavailable() {
  const { t } = useTranslation();
  usePageTitle(t("profile.unavailable.title"));
  return (
    <PageShell testId="booking-profile" state="unavailable">
      <PageHead
        title={t("profile.unavailable.title")}
        sub={t("profile.unavailable.body")}
      />
      {t("profile.unavailable.hint") ? <p className="b2-hint">{t("profile.unavailable.hint")}</p> : null}
    </PageShell>
  );
}

/*
 * TRANSPORT, and the only refusal that may retry. Web v2 draws this as "Lura
 * didn't answer. This is on our side, not yours." That is not known here: a
 * dropped connection on the visitor's side produces the same failure, so the
 * existing copy (which says to check the connection) stays. v2's "the
 * business can still book you in" is a claim about someone else's practice
 * and is not rendered either.
 */
function Failed({ retry }: { retry: () => void }) {
  return (
    <PageShell testId="booking-profile" state="error">
      <LoadFailed title="profile.error.title" body="profile.error.body" retry="profile.error.retry" onRetry={retry} testId="profile-error" autoReload="profile" />
    </PageShell>
  );
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters =
    words.length > 1 ? [words[0], words[words.length - 1]] : words;
  return letters
    .map((word) => Array.from(word ?? "")[0] ?? "")
    .join("")
    .toUpperCase();
}

/*
 * NOT_BOOKABLE. Web v2 · 04 · "Booking is paused". The words are Product's
 * approved rc.9 copy (2026-10-15, docs/i18n/copy-review-rc9-2026-10-15.csv):
 * "Online booking is paused" / "For appointments, contact {business} directly."
 *
 * A real professional, so they are named — their identity is already public,
 * and naming them turns a dead end into "come back later". No warning tone,
 * because nothing has gone wrong, and no disabled Book button: v1 kept one on
 * screen and a control that cannot work is a dead control.
 *
 * NOT_BOOKABLE carries no cause, so nothing here says WHO paused booking or
 * that it is "temporary"; the contract says only that a real professional is
 * not currently taking bookings ("paused" is Product's word for that state).
 * v2's "Any appointment you already have is unaffected" is also not
 * rendered: this page does not know that, and a client relying on it would be
 * relying on us.
 */
function Paused({ profile }: { profile: PublicProfessionalProfile }) {
  const { t } = useTranslation();
  usePageTitle(t("profile.paused.title"));
  return (
    <PageShell testId="booking-profile" state="not-bookable">
      <div className="b2-who">
        <span className="b2-avatar" aria-hidden="true">
          {initials(profile.businessName)}
        </span>
        <div className="b2-who-b">
          <span className="b2-who-name">{profile.businessName}</span>
          {profile.addressDisplay ? (
            <span className="b2-hint">{profile.addressDisplay}</span>
          ) : null}
        </div>
      </div>
      <PageHead
        title={t("profile.paused.title")}
        sub={t("profile.paused.body", { business: profile.businessName })}
      />
    </PageShell>
  );
}

function ProfileBody({
  slug,
  profile,
}: {
  slug: string;
  profile: PublicProfessionalProfile;
}) {
  const { t, fmt } = useTranslation();
  usePageTitle(profile.businessName);
  const gated = profile.requiresClientAccount;
  // The session row (components/SessionRow.tsx) for a signed-in client, on
  // the account-gated professional's landing (a professional who accepts
  // guests is sent on to the service step, which carries its own). Nothing is
  // held here, so Sign out only signs out, back to this professional's
  // sign-in gate. The target is keyed on `gated`, so it stays right if this
  // page is ever shown for a professional who accepts guests.
  const { status, account } = useIdentity();
  const { signOut } = useBookSignOut({ slug, requiresAccount: gated });
  const session = status === "account" && account !== null ? { email: account.email, signOut } : null;
  // Sign-in is this page's only way on when the professional requires an
  // account: its form's chunk starts now (entry headroom recovery, 2026-10-28).
  useEffect(() => {
    if (gated) signInChunk.prefetch();
  }, [gated]);

  // The step-shaped page (PageShell `flow`; c3 folded the local shell in).
  // The pinned bar is a SIBLING of the scroll area, as the comp draws it on
  // the phone too, and on desktop a grid item the layer places in the rail.
  return (
    <SignOutContext.Provider value={session}>
    <PageShell
      layout="flow"
      testId="booking-profile"
      state="ready"
      foot={
        <div className="b-foot">
          {/* Account-gated professionals enter same-origin sign-in before slot
              selection. A paused professional never reaches this component. */}
          {!gated ? (
            <>
              <Link
                className="b-btn v-primary is-full"
                to={`/${slug}/book/service`}
                data-testid="profile-book"
              >
                {t("profile.book")}
              </Link>
              <Link className="b-btn v-secondary is-full" to={`/${slug}/book/signin`}
                data-testid="profile-signin">
                {t("profile.signInToBook")}
              </Link>
            </>
          ) : (
            <Link
              className="b-btn v-primary is-full"
              to={`/${slug}/book/signin`}
              data-testid="profile-book"
            >
              {t("profile.bookGated")}
            </Link>
          )}
        </div>
      }
    >
      <div className="b-stack g6">
        <h1 className="b-display">{profile.businessName}</h1>
        {profile.addressDisplay ? (
          <p className="b-meta">{profile.addressDisplay}</p>
        ) : null}
        <div className="b-pills">
          {profile.acceptsInAppPayments ? null : (
            <span className="b-pill">{t("profile.pill.payAtAppointment")}</span>
          )}
          {/* Product: account gating is surfaced BEFORE slot selection, so a
              client is never walked through picking a time and stopped at the
              end. This pill plus the notice below are that promise on the
              profile page. */}
          {gated ? (
            <span className="b-pill t-info">
              {t("profile.pill.accountRequired")}
            </span>
          ) : null}
        </div>
      </div>

      {profile.businessDescription ? (
        <p className="b-p">{profile.businessDescription}</p>
      ) : null}

      {gated ? (
        <div className="b-notice t-info">
          <div className="b-notice-b">
            <b>
              {t("profile.gated.title", { business: profile.businessName })}
            </b>
            <p className="b-p">{t("profile.gated.body")}</p>
          </div>
        </div>
      ) : null}

      {profile.services.length > 0 ? (
        <div className="b-stack g6">
          <h2 className="b3-service-heading">{t("flow.service.heading")}</h2>
          <div className="b-services">
            {profile.services.map((service) => (
              <div className="b-card" key={service.id}>
                <div className="b-row">
                  <div className="b-stack g6" style={{ flex: 1 }}>
                    <span className="b-h3">{service.name}</span>
                    <span className="b-meta">
                      {fmt.duration(service.durationMinutes)}
                    </span>
                  </div>
                  <span className="b-money">
                    {fmt.money(service.priceInCents)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <ConsentBanner />
      <Promo />
    </PageShell>
    </SignOutContext.Provider>
  );
}

function Profile() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { locale } = useTranslation();
  const state = useBookingProfile(isPossibleSlug(slug) ? slug : undefined, locale === "es" ? "ES" : "EN");

  // MOVED: the professional renamed, and every card, QR code and message
  // already shared points at the old name. Replace rather than push, so Back
  // does not bounce the visitor between the two.
  const canonical = state.status === "moved" ? state.canonicalSlug : null;
  useEffect(() => {
    if (canonical) navigate(`/${canonical}${search}`, { replace: true });
  }, [canonical, navigate, search]);

  // The route matches path SHAPE, so every unmatched root path arrives here —
  // /robots.txt, /apple-touch-icon-precomposed.png (which iOS probes
  // unprompted), uppercase names. A string that cannot be a slug is a
  // wrong-shaped URL, not an unknown professional, and must not cost a
  // resolver round trip.
  if (!isPossibleSlug(slug)) return <NotFound />;

  switch (state.status) {
    case "loading":
      return <Loading />;
    case "moved":
      // The effect above navigates; this covers the render before it runs and
      // the no-JS case.
      return <Navigate to={`/${state.canonicalSlug}${search}`} replace />;
    case "unavailable":
      return <Unavailable />;
    case "error":
      return <Failed retry={state.retry} />;
    case "not-bookable":
      return <Paused profile={state.profile} />;
    case "ready":
      return state.profile.requiresClientAccount
        ? <ProfileBody slug={state.slug} profile={state.profile} />
        : <Navigate to={`/${state.slug}/book/service${search}`} replace />;
  }
}

export default Profile;
